import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PDFViewer from './PDFViewer';
import '../index.css'; // pastikan CSS animasi ada di sini

const About = () => {
  const [showPDFViewer, setShowPDFViewer] = useState(false);

  return (
    <div className="min-h-screen bg-black text-white py-16 px-4 pb-24">
      <div className="max-w-4xl mx-auto">

        {/* SEO-friendly header */}
        <section>
          <header className="text-center mb-12">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1"></div>
              <h1 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-600">
                About Sabtian
              </h1>
              <div className="flex-1 flex justify-end">
                <Link 
                  to="/" 
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                >
                  ✕ Close
                </Link>
              </div>
            </div>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              My Experience Overview
            </p>
          </header>
        </section>

        {/* Main content */}
        <section>
          <div className="grid md:grid-cols-2 gap-12 items-start">

            {/* Photo and basic info */}
            <div className="text-center md:text-left">
              <div className="relative inline-block mb-6 animate-slide-in-left">
                <img 
                  src="/asset/Kemeja Berwarna.png"
                  alt="Sabtian Adiansyah - Business Analyst/Business System Analyst"
                  className="w-64 h-64 rounded-full object-cover border-4 border-white-500 shadow-2xl mx-auto md:mx-0"
                  loading="lazy"
                />
                <div className="absolute -bottom-2 -right-2 bg-green-500 w-8 h-8 rounded-full border-4 border-black flex items-center justify-center">
                  <span className="text-xs">✓</span>
                </div>
              </div>

              <div className="space-y-4 animate-slide-in-left" style={{ animationDelay: '0.1s' }}>
                <h2 className="text-2xl font-semibold">Sabtian Adiansyah</h2>
                <h3 className="text-blue-400 font-medium">Business Analyst | Business System Analyst</h3>
                <div className="flex flex-wrap justify-center md:justify-start gap-2">
                  <span className="bg-gray-800 px-3 py-1 rounded-full text-sm">📍 Jakarta, Indonesia</span>
                  <span className="bg-gray-800 px-3 py-1 rounded-full text-sm">🌐 Remote Available</span>
                </div>
              </div>
            </div>

            {/* Description, Core Expertise & Projects */}
            <div className="space-y-8">

              {/* Professional Summary */}
              <section className="animate-slide-in-right">
                <h2 className="text-2xl font-semibold mb-4 text-blue-400">Professional Summary</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  I'm Sabtian, Business Analyst with 5+ years of experience in data analysis, dashboard automation, and operational optimization. 
                </p>
                <p className="text-gray-300 leading-relaxed">
                  My work blends no-code/low-code platforms like n8n with Python scripting, Google tools, and AI agents. 
                  I thrive in cross-functional teams where impact and iteration matter more than formality.
                </p>
              </section>

              {/* Core Expertise */}
              <section className="animate-slide-in-right" style={{ animationDelay: '0.1s' }}>
                <h2 className="text-2xl font-semibold mb-4 text-purple-400">Core Expertise</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h3 className="font-semibold text-green-400">Data & Automation</h3>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• SQL</li>
                      <li>• Python</li>
                      <li>• n8n</li>
                      <li>• AI Agent</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-semibold text-green-400">Google Platform</h3>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Google Looker Studio</li>
                      <li>• Google Spreadsheets</li>
                      <li>• Google Analytics</li>
                      <li>• Google BigQuery</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Projects */}
              <section className="animate-slide-in-up" style={{ animationDelay: '0.2s' }}>
                <h2 className="text-2xl font-semibold mb-4 text-orange-400">Projects</h2>

                <div className="space-y-6">

                  <div>
                    <h3 className="font-semibold text-green-400">AI Automation using n8n</h3>
                    <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
                      <li>Instagram, Telegram, & LinkedIn Contact Leads Scraper</li>
                      <li>Automated meeting transcript summarization & analysis</li>

                      <li>
                        WhatsApp & Instagram AI Chatbot
                        <ul className="list-disc list-inside ml-5">
                          <li>Able to process multiple message types (text, images, audio, documents)</li>
                          <li>Retrieval-Augmented Generation (RAG)</li>
                          <li>Respond using internal knowledge base integrated with vector databases</li>
                          <li>Ticketing workflow & Round-Robin assignment</li>
                          <li>Create Google Calendar schedules directly from chatbot messages</li>
                        </ul>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-green-400">Internal Process Automation using Python</h3>
                    <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
                      <li>
                        WhatsApp automated blast messaging & follow-ups
                        <ul className="list-disc list-inside ml-5">
                          <li>Follow-ups triggered based on date, time, and template messages from spreadsheets</li>
                          <li>Automatically updates status to "Delivered" in spreadsheets when the message is sent</li>
                        </ul>
                      </li>
                      <li>CV Parsing using Python</li>
                      <li>Data Categorization Automation</li>
                      <li>WhatsApp Group Contact Scraper</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-green-400">Dashboards</h3>
                    <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
                      <li>Google Looker Studio Dashboards</li>
                      <li>Google Sheets Interactive Dashboards (Pivot tables, slicers, and visual reports)</li>
                    </ul>
                  </div>

                </div>
              </section>

            </div> {/* END div space-y-8 */}

          </div>
        </section>

        {/* Call to action */}
        <section>
          <div className="mt-16 text-center animate-slide-in-up" style={{ animationDelay: '0.3s' }}>
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8">
              <h2 className="text-2xl font-bold mb-4">Ask Questions & Download Resume</h2>
              <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                Interested in my work or have a collaboration in mind? Let’s get in touch!
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=sabtianadiansyah@gmail.com&su=Let%27s%20Connect!&body=Hi%20Sabtian%2C%20I%27d%20love%20to%20discuss..."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
                  aria-label="Send email via Gmail"
                >
                  📧 Send via Gmail
                </a>

                <button 
                  type="button"
                  onClick={() => setShowPDFViewer(true)}
                  className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white hover:text-blue-600 transition-colors inline-flex items-center justify-center"
                  aria-label="View Sabtian's portfolio resume"
                >
                  📄 View Resume
                </button>
              </div>

            </div>
          </div>
        </section>

      </div>

      {/* PDF Viewer Modal */}
      {showPDFViewer && (
        <PDFViewer
          pdfUrl="/asset/CV Sabtian Adiansyah.pdf"
          fileName="CV Sabtian Adiansyah.pdf"
          onClose={() => setShowPDFViewer(false)}
        />
      )}

    </div>
  );
};

export default About;
